import { formatNameSummary, getCollectionLabel } from "./activityUtils";

const NOTIFICATION_SCHEMA_VERSION = 2;

export const buildAddNotificationPayload = ({ collectionName, data, actorName }) => {
  const label = getCollectionLabel(collectionName);
  const itemName = data.name || data.title || data.category || "항목";

  let title = `${label} 등록`;
  let message = `${actorName || "팀원"}님이 '${itemName}'을(를) 등록했습니다.`;
  let category = "activity";
  let targetId = null;
  let targetMenu = null;

  if (collectionName === "students") {
    title = "새로 온 가족을 환영해요! 🎉";
    message = `${actorName || "교사"}님이 새로운 학생 '${itemName}'님을 명단에 모셨습니다.`;
    targetId = data.id || null;
    targetMenu = "students";
  } else if (collectionName === "teachers") {
    title = "동역자 합류를 환영해요! 🤝";
    message = `${actorName || "관리자"}님이 새로운 선생님 '${itemName}'님과 함께하게 되었습니다.`;
    targetId = data.id || null;
    targetMenu = "teachers";
  } else if (collectionName === "finances") {
    title = "꼼꼼한 재정 기록 💰";
    message = `${actorName || "팀원"}님이 새로운 내역('${itemName}')을 기록하셨네요. 늘 감사합니다!`;
  } else if (collectionName === "visitLogs") {
    title = "목양의 손길이 닿았어요 💖";
    const studentName = data.studentName || "학생";
    const logType = data.type || "심방";
    message = `${actorName || "교사"}님이 '${studentName}'님께 ${logType}으로 마음을 전했습니다.`;
    category = "pastoral";
  }

  return {
    title,
    message,
    type: "success",
    category,
    targetId,
    targetMenu,
    notificationVersion: NOTIFICATION_SCHEMA_VERSION,
  };
};

export const buildBulkAddNotificationPayload = ({ collectionName, names, actorName }) => {
  const label = getCollectionLabel(collectionName);
  const displayNames = formatNameSummary(names);

  let title = `${label} 일괄 등록 ✨`;
  let message = `${actorName || "팀원"}님이 ${displayNames}님을 새롭게 모셨습니다. 🎉`;
  let targetMenu = null;

  if (collectionName === "students") {
    title = "새 식구들이 많이 왔어요! 🎊";
    message = `${actorName || "교사"}님이 ${displayNames} 학생들을 명단에 새롭게 등록했습니다. 모두 환영해 주세요!`;
    targetMenu = "students";
  } else if (collectionName === "teachers") {
    title = "새로운 동역자들을 환영해요! 🤝";
    message = `${actorName || "관리자"}님이 ${displayNames} 선생님들을 새롭게 모셨습니다. 우리 팀이 더 든든해졌네요!`;
    targetMenu = "teachers";
  }

  return {
    title,
    message,
    type: "success",
    category: "activity",
    targetMenu,
    notificationVersion: NOTIFICATION_SCHEMA_VERSION,
  };
};

export const buildUpdateNotificationPayload = ({ collectionName, data, actorName }) => {
  const label = getCollectionLabel(collectionName);
  const itemName = data.name || data.title || data.category || "항목";

  let title = `${label} 정보 수정`;
  let message = `${actorName || "팀원"}님이 '${itemName}'님의 정보를 최신으로 업데이트하셨어요. ✨`;
  let targetId = null;
  let targetMenu = null;

  if (collectionName === "students") {
    title = "성장의 기록이 업데이트됐어요 🌱";
    message = `${actorName || "교사"}님이 '${itemName}'님의 새로운 모습을 기록했어요.`;
    targetId = data.id || null;
    targetMenu = "students";
  } else if (collectionName === "teachers") {
    title = "동역자 정보가 업데이트됐어요 📝";
    message = `${actorName || "관리자"}님이 '${itemName}' 선생님의 정보를 수정했어요.`;
    targetId = data.id || null;
    targetMenu = "teachers";
  } else if (collectionName === "schedules") {
    title = "일정이 수정됐어요 📅";
    message = `'${itemName}' 일정에 변경이 생겼어요. 확인해 보세요!`;
  } else if (collectionName === "visitLogs") {
    title = "심방 기록이 수정됐어요 📝";
    message = `${actorName || "교사"}님이 심방 기록을 보완했어요.`;
  }

  return {
    title,
    message,
    type: "info",
    category: "activity",
    targetId,
    targetMenu,
    notificationVersion: NOTIFICATION_SCHEMA_VERSION,
  };
};

export const buildDeleteNotificationPayload = ({ collectionName, actorName }) => {
  const label = getCollectionLabel(collectionName);

  let title = `${label} 정리 완료`;
  let message = `${actorName || "팀원"}님이 ${label} 목록을 정리하셨습니다.`;
  let targetMenu = null;

  if (collectionName === "students" || collectionName === "teachers") {
    title = "명단이 정리됐어요 🗂️";
    message = `${actorName || "팀원"}님이 명단을 깔끔하게 정리했어요.`;
    targetMenu = collectionName;
  } else if (collectionName === "schedules") {
    title = "일정이 정리됐어요 📅";
    message = `${actorName || "팀원"}님이 지난 일정을 정리했어요.`;
    targetMenu = "schedule";
  } else if (collectionName === "finances") {
    title = "재정 내역이 정리됐어요 💰";
    message = `${actorName || "팀원"}님이 재정 기록을 정리했어요.`;
    targetMenu = "finance";
  }

  return {
    title,
    message,
    type: "info",
    category: "activity",
    targetMenu,
    notificationVersion: NOTIFICATION_SCHEMA_VERSION,
  };
};

export const buildBulkDeleteNotificationPayload = ({ collectionName, names, actorName }) => {
  const label = getCollectionLabel(collectionName);
  const displayNames = formatNameSummary(names);

  const title = `${label} 일괄 정리 완료 🗂️`;
  const message = `${actorName || "팀원"}님이 ${displayNames} 명단을 깔끔하게 정리하셨습니다.`;
  let targetMenu = null;

  if (collectionName === "students" || collectionName === "teachers") {
    targetMenu = collectionName;
  } else if (collectionName === "schedules") {
    targetMenu = "schedule";
  } else if (collectionName === "finances") {
    targetMenu = "finance";
  }

  return {
    title,
    message,
    type: "info",
    category: "activity",
    targetMenu,
    notificationVersion: NOTIFICATION_SCHEMA_VERSION,
  };
};
